import {
  KnownKioskTransactionType,
  KnownProjectDtoProjectType,
  KnownProjectModuleSettingsDtoName
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useCallback, useMemo } from 'react';
import { useQueryGetProject } from '../../../../core/queries/projects.queries';
import { useQueryListCarriers } from '../../../carriers/carriers.queries';
import {
  useMutationCreateProjectModule,
  useQueryGetProjectModules,
  useQueryListAllProjectModules
} from '../../../project-modules/queries';
import { SettingsMapper } from '../../mappings/mappings';
import { EntityForm } from '../form.types';
import { ProjectModuleForm } from './form';
import { ProjectModuleFormOutput } from './form-output';
import { ProjectModuleFormSchema } from './form-schema';
import { projectModuleFormUISchema } from './ui-schema';

export function useCreateProjectModuleForm(
  projectID: string | undefined,
  projectModulesFolderNodeID: string | undefined,
  formData: ProjectModuleForm | undefined
): EntityForm | null {
  const defaultValues = useMemo((): ProjectModuleForm => {
    return {
      occupantRules: 'all',
      externalDeliveryContainerRules: 'all',
      internalDeliveryContainerRules: 'all',
      // Temporary: we are disabling setting Team Member Rules for now
      teamMemberRules: 'none'
    };
  }, []);

  const initialFormOutput = useMemo(() => {
    return new ProjectModuleFormOutput(defaultValues);
  }, [defaultValues]);

  const currentFormOutput = useMemo(() => {
    return new ProjectModuleFormOutput({
      ...initialFormOutput,
      ...formData
    });
  }, [formData, initialFormOutput]);

  const modules = useQueryListAllProjectModules();
  const modulesForProject = useQueryGetProjectModules(projectID);
  // We only want to fetch Carriers if the selected module is Dry Cleaning
  const parties = useQueryListCarriers(projectID, {
    enabled:
      modules.data?.find((m) => m.id === currentFormOutput.schemaID)?.name ===
      KnownProjectModuleSettingsDtoName.DryCleaning,
    allowedTransactionTypes: [KnownKioskTransactionType.DeliverInboundDryCleaning]
  });
  const project = useQueryGetProject(projectID);

  const availableModules = useMemo(() => {
    return (
      modules.data?.filter((module) => {
        // Filter out modules that are already part of the project
        return !modulesForProject.data?.some((projectModule) => projectModule.schema.name === module.name);
      }) || []
    );
  }, [modules.data, modulesForProject.data]);

  const carrierOptions = useMemo(() => {
    return (parties.data?.items || []).map((carrier) => ({
      type: 'string' as const,
      title: carrier.name,
      enum: [carrier.id] as [string]
    }));
  }, [parties.data?.items]);

  const formSchema = useMemo(() => {
    return ProjectModuleFormSchema.FromSettings(
      currentFormOutput,
      availableModules,
      carrierOptions,
      project.data?.projectType as KnownProjectDtoProjectType | undefined
    );
  }, [currentFormOutput, availableModules, carrierOptions, project.data]);

  const mutation = useMutationCreateProjectModule();

  const onSubmit = useCallback(
    async (formOutput: ProjectModuleFormOutput) => {
      if (!projectID) {
        return;
      }
      const { schemaID, ...settings } = formOutput;
      if (!schemaID) {
        throw new Error('Schema ID is required to create a project module.');
      }
      const data = ProjectModuleFormOutput.ToSettingsData(settings);
      const result = await mutation.mutateAsync({
        projectID,
        settingsSchemaID: schemaID,
        settings: data
      });
      return {
        entityId: result.id,
        parentNodeId: projectModulesFolderNodeID
      };
    },
    [mutation, projectID, projectModulesFolderNodeID]
  );

  if (!formSchema) {
    return null;
  }

  return {
    isLoading: modules.isLoading || parties.isLoading || project.isLoading,
    title: 'Add Project Module',
    defaultValues,
    initialFormOutput: initialFormOutput,
    formSchema,
    uiSchema: projectModuleFormUISchema,
    getOutput(formData) {
      return new ProjectModuleFormOutput(formData);
    },
    onChangeInterceptor: (_, formData) => formData, // No specific interceptor needed
    onSubmit,
    isLocked: SettingsMapper.ProjectTypeToProjectModuleLockedStatus(
      project.data?.projectType as KnownProjectDtoProjectType | undefined
    ),
    lockedMessage: SettingsMapper.ProjectTypeToProjectModuleLockedMessage(
      project.data?.projectType as KnownProjectDtoProjectType | undefined
    )
  };
}
